import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {Panel, PanelHeader} from '@Components/panel';
import {getGridItemStorageForId, getSelectedGridItems} from '@Components/base-grid';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {toggleIsFbPanelOpenState, updateCurrentCollection, updateIsForDesignUpload} from '@Panels/user-media-panel/user-media-panel-reducer';
import {selectTab} from '@Components/tabs/tabs-reducer';
import {
  COLLECTION_GRID_ID,
  UploadsMediaSubTab,
  UploadsMediaType,
  USER_UPLOADS_MEDIA_SUB_TABS,
  USER_UPLOADS_MEDIA_TABS,
  USER_UPLOADS_SELECTION_GRID_ID,
  UserMediaPanelType,
} from '@Panels/user-media-panel/user-media-panel.types';
import {UploadAndSocialMedia} from '@Panels/user-media-panel/components/upload-and-social-media-section';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {TabsSection} from '@Panels/user-media-panel/components/tabs-section';
import type {BasePanel} from '@Components/panel/panel.types';
import {MediaPanelFooter} from '@Panels/user-media-panel/components/media-panel-footer';
import {GA4EventName, GA4EventParam, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {CollectionSection} from '@Panels/user-media-panel/components/collection-section';
import {BrandLogoSection} from '@Panels/user-media-panel/components/brand-logo-section';
import {MediaSection} from '@Panels/user-media-panel/components/media-section';
import {useSelectedTab} from '@Components/tabs';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {GridUserMediaItemStorage} from '@Libraries/user-media-library';
import {gridUserMediaItemStorageToMediaData, MultiMediaUploadSource, UserMediaType} from '@Libraries/user-media-library';
import {openShareCollectionModal} from '@Modals/share-collection-modal';
import type {ElementData, TempImageData} from '@Libraries/add-media-library';
import {isTempImageData} from '@Libraries/add-media-library';
import {FacebookMediaSection} from '@Panels/user-media-panel/components/facebook-media-section';
import {RECORD_DROPDOWN_OPTIONS} from '@Panels/user-media-panel/components/record-dropdown';
import RecordAudioPanel from '@Panels/record-audio-panel';
import RecordImageAndVideoPanel from '@Panels/record-image-and-video-panel';
import {updateOptionsPanelHeight, updateOptionsPanelStickToBottom} from '@Components/poster-editor/library/poster-editor-library';
import {RecorderMediaType} from '@Panels/record-image-and-video-panel/record-image-and-video.types';
import type {GridCollectionItemStorage} from '@Components/base-grid/components/grid-collection-item';
import type {PanelBottomPill} from '@Components/panel/components/panel-bottom-pills';
import useWindowSize from '@Hooks/useWindowSize';
import {isOpenCustomImageAndVideoRecorder, isOpenCustomImageAndVideoRecorderSmall} from '@Libraries/record-image-and-video.library';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {openRecordImageAndVideoModal} from '@Modals/record-image-and-video-modal';
import {useHiddenImageAndVideoInput} from '@Hooks/poster-editor/useHiddenImageAndVideoInput';
import StockPanel from '@Panels/stock-panel';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import styles from './user-media-panel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import AiVoicePanel from '@Panels/ai-voice-panel';
import {addGeneratedVoiceToDesign} from '@Libraries/text-to-speech.library';
import {getAiVoicePanelId} from '@Panels/user-media-panel/user-media.library';

const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 700,
  },
  large: {
    minWidth: 701,
  },
};

export interface UserMediaPanelProps extends BasePanel {
  uploadAcceptedMimetype?: string;
  maxSelectionNumber?: number;
  showVectors?: boolean;
  defaultTab: UploadsMediaType;
  /**
   * Multiple files uploaded will be added on canvas together instead of adding each file separately on canvas once its uploaded
   */
  addInBulk?: boolean;
  shouldLoadDataUrlImage?: boolean;
  mediaTypesToShow?: UploadsMediaType[];
  bottomPills?: PanelBottomPill[];
  hideRecordingOptions?: boolean;
  uploadSource?: MultiMediaUploadSource;
  onAddSelectedCTAText?: string;
  videoQualityFiltersDisabled?: boolean;
  isForDesignUpload?: boolean;
  hideFooterSecondaryActions?: boolean;

  onStartAddSelected?: () => void;

  onUploadError?: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;

  onAddSelected(items: ElementData[], currentUploadIndex?: number, totalUploadsCount?: number): void | Promise<void>;

  onAddBrandAsset?(): void;

  onBack?(): void;

  onClose?(): void;
  defaultActiveSubTab?: UploadsMediaSubTab;
}

function UserMediaPanel({
  uploadSource = MultiMediaUploadSource.UPLOAD,
  mediaTypesToShow = [UploadsMediaType.IMAGE, UploadsMediaType.VIDEO, UploadsMediaType.AUDIO],
  showVectors = true,
  hideRecordingOptions = false,
  onStartAddSelected = (): void => {},
  videoQualityFiltersDisabled = false,
  isForDesignUpload = false,
  onUploadError = (): void => {},
  hideFooterSecondaryActions = false,
  ...props
}: UserMediaPanelProps): ReactElement {
  const panelId = useRef(props.panelId ?? uuidv4());
  const onClose = props.onClose?.bind(null) ?? useDefaultPanelClose(panelId.current, props.hideOnClose);

  const currentCollectionId = useAppSelector((state) => {
    return state.mediaUploads.collection;
  });
  const isFbPanelOpen = useAppSelector((state) => {
    return state.mediaUploads.isFbPanelOpen;
  });
  const currentCollection = useAppSelector((state) => {
    return getGridItemStorageForId(state, COLLECTION_GRID_ID, currentCollectionId) as GridCollectionItemStorage;
  });
  const selectedGridItems = useAppSelector((state) => {
    return getSelectedGridItems(state, USER_UPLOADS_SELECTION_GRID_ID);
  });
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, props.defaultTab) as UploadsMediaType;
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);
  const contentRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const [panelToShow, setPanelToShow] = useState<UserMediaPanelType>(UserMediaPanelType.DEFAULT);
  const {windowWidth} = useWindowSize();
  const hiddenImageAndVideoInput = useHiddenImageAndVideoInput((data): void => {
    props.onAddSelected([data]);
  });

  useEffect(() => {
    dispatch(selectTab(USER_UPLOADS_MEDIA_TABS, props.defaultTab));
    dispatch(updateIsForDesignUpload(isForDesignUpload));
    trackPosterBuilderGA4Events(GA4EventName.UPLOADS_MODAL_OPEN);

    return (): void => {
      dispatch(selectTab(USER_UPLOADS_MEDIA_TABS, UploadsMediaType.IMAGE));
      dispatch(selectTab(USER_UPLOADS_MEDIA_SUB_TABS, props.defaultActiveSubTab ?? UploadsMediaSubTab.UPLOADS));
      dispatch(toggleIsFbPanelOpenState(false));
      dispatch(updateCurrentCollection(''));
    };
  }, []);

  const gridItemsToElementData = (items: GridItemStorage[]): ElementData[] => {
    const data: ElementData[] = [];
    items.forEach((item) => {
      data.push(gridUserMediaItemStorageToMediaData(item));
    });
    return data;
  };

  const getGATrackingTermForItem = (type: GRID_ITEM_TYPE): string => {
    switch (type) {
      case GRID_ITEM_TYPE.USER_VIDEO:
        return 'Video';
      case GRID_ITEM_TYPE.USER_AUDIO:
        return 'Audio';
      case GRID_ITEM_TYPE.USER_IMAGE:
      default:
        return 'Photo';
    }
  };

  const trackGA4Event = (): void => {
    for (const item of selectedGridItems) {
      trackPosterBuilderGA4Events(`UploadsModal_${getGATrackingTermForItem(item.type)}Add`);
    }
  };

  const addSelectedItems = (items?: ElementData[]): void => {
    onStartAddSelected();
    trackGA4Event();
    props.onAddSelected(items ?? gridItemsToElementData(selectedGridItems));
  };

  const onBackButton = (): void => {
    if (isInCollection()) {
      dispatch(updateCurrentCollection(''));
      dispatch(selectTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.COLLECTION));
    } else if (props.onBack) {
      props.onBack();
    }
  };

  const isInCollection = (): boolean => {
    return !!currentCollectionId.length;
  };

  const isShowingBackgroundImages = (): boolean => {
    return props.defaultTab === UploadsMediaType.IMAGE_BACKGROUND;
  };

  const MAP_UPLOADS_MEDIA_TYPE_TO_MEDIA_TYPE = {
    [UploadsMediaType.IMAGE]: UserMediaType.IMAGE,
    [UploadsMediaType.IMAGE_BACKGROUND]: UserMediaType.IMAGE_BACKGROUND,
    [UploadsMediaType.VIDEO]: UserMediaType.VIDEO,
    [UploadsMediaType.AUDIO]: UserMediaType.AUDIO,
  };

  const getHeader = (): ReactElement => {
    let title = window.i18next.t('pmwjs_myuploads_capitalized');
    const actions = [];
    if (isInCollection()) {
      title = currentCollection.collectionName;
    }
    if (activeTab === UploadsMediaType.IMAGE_BACKGROUND) {
      title = window.i18next.t('pmwjs_my_backgrounds');
    }
    if (activeTab === UploadsMediaType.IMAGE_BACKGROUND && params.small) {
      title = window.i18next.t('pmwjs_image');
    }
    if (currentCollectionId && !currentCollection.isShared) {
      actions.push({
        key: 'share-collection',
        text: window.i18next.t('pmwjs_share'),
        icon: 'icon-group',
        onClick: (): void => {
          openShareCollectionModal({collectionId: currentCollectionId, collectionType: MAP_UPLOADS_MEDIA_TYPE_TO_MEDIA_TYPE[activeTab]});
        },
      });
    }
    return <PanelHeader title={title} showBackButton={isInCollection() || !!props.onBack} onBackButton={onBackButton} onClose={onClose} actions={actions} />;
  };

  const getGrids = (): ReactElement => {
    return (
      <div className="_full-width _full-height flex-v-row">
        <CollectionSection />
        <BrandLogoSection
          addSelectedItems={(): void => {
            addSelectedItems();
            onClose();
          }}
          maxSelectionNumber={props.maxSelectionNumber}
          defaultActiveSubTab={props.defaultActiveSubTab}
        />
        <MediaSection
          videoQualityFiltersDisabled={videoQualityFiltersDisabled}
          excludeItemWithExtension={showVectors ? '' : 'svg'}
          maxSelectionNumber={props.maxSelectionNumber}
          addSelectedItems={addSelectedItems}
          scrollingContainer={contentRef.current}
          mediaTypesToShow={mediaTypesToShow}
          defaultTab={props.defaultTab}
          addInBulk={!!props.addInBulk}
          onClose={onClose}
          shouldLoadDataUrlImage={props.shouldLoadDataUrlImage}
          onGenerateAIImage={(e) => {
            e.stopPropagation();
            setPanelToShow(UserMediaPanelType.AI_IMAGE);
          }}
        />
      </div>
    );
  };

  const hideSocialMediaOptions = (): boolean => {
    return isShowingBackgroundImages() || !mediaTypesToShow.includes(UploadsMediaType.IMAGE);
  };

  const onClickRecordImageAndVideo = (openWithMediaType: UserMediaPanelType): void => {
    if (isOpenCustomImageAndVideoRecorderSmall(windowWidth)) {
      openRecordImageAndVideoModal({
        onStartAddSelected,
        hideToggle: currentCollectionId !== '',
        recorderOpenedWithMediaType: openWithMediaType === UserMediaPanelType.RECORD_PHOTO ? RecorderMediaType.IMAGE : RecorderMediaType.VIDEO,
        onAddSelected: (data): void => {
          props.onAddSelected([data]);
        },
      });
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    } else if (isOpenCustomImageAndVideoRecorder()) {
      setPanelToShow(openWithMediaType);
      if (props.panelId === 'user-media-modal-panel') {
        updateOptionsPanelStickToBottom(props.panelId ?? '', false);
      }
    } else if (openWithMediaType === UserMediaPanelType.RECORD_PHOTO || openWithMediaType === UserMediaPanelType.RECORD_VIDEO) {
      hiddenImageAndVideoInput.handleHiddenInputTagClick(openWithMediaType === UserMediaPanelType.RECORD_PHOTO ? RecorderMediaType.IMAGE : RecorderMediaType.VIDEO, props.onClose);
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };
  const onClickRecordOptions = (id: RECORD_DROPDOWN_OPTIONS): void => {
    switch (id) {
      case RECORD_DROPDOWN_OPTIONS.VIDEO:
        trackPosterBuilderGA4Events(GA4EventName.UPLOADSMODAL_RECORDED_MEDIA, {
          [GA4EventParamName.TYPE]: GA4EventParam.VIDEO,
        });
        onClickRecordImageAndVideo(UserMediaPanelType.RECORD_VIDEO);
        break;
      case RECORD_DROPDOWN_OPTIONS.AUDIO:
        trackPosterBuilderGA4Events(GA4EventName.UPLOADSMODAL_RECORDED_MEDIA, {
          [GA4EventParamName.TYPE]: GA4EventParam.AUDIO,
        });
        setPanelToShow(UserMediaPanelType.RECORD_AUDIO);
        break;
      case RECORD_DROPDOWN_OPTIONS.PHOTO:
        trackPosterBuilderGA4Events(GA4EventName.UPLOADSMODAL_RECORDED_MEDIA, {
          [GA4EventParamName.TYPE]: GA4EventParam.IMAGE,
        });
        onClickRecordImageAndVideo(UserMediaPanelType.RECORD_PHOTO);
        break;
      default:
        break;
    }
  };

  const getContent = (): ReactElement => {
    return (
      <div className={`_full-width _full-height flex-v-row ${styles.container} ${params.small ? 'spacing-p-4' : 'spacing-p-t-8 spacing-p-l-12 spacing-p-r-12'}`} ref={contentRef}>
        <UploadAndSocialMedia
          onStartAddSelected={onStartAddSelected}
          uploadSource={uploadSource}
          hideRecordingOptions={hideRecordingOptions}
          panelId={panelId.current}
          onMediaUploaded={(items: (GridItemStorage | TempImageData)[], currentUploadIndex?: number, totalUploadsCount?: number): void => {
            const tempImageDataArray: TempImageData[] = [];
            let index = items.findIndex(isTempImageData);
            while (index !== -1) {
              tempImageDataArray.push(...(items.splice(index, 1) as TempImageData[]));
              index = items.findIndex(isTempImageData);
            }
            // filtered out TempImageData elements from items array in the above step
            const gridItemsArray = gridItemsToElementData(items as GridItemStorage[]);

            void props.onAddSelected([...gridItemsArray, ...tempImageDataArray], currentUploadIndex, totalUploadsCount);
          }}
          onUploadError={onUploadError}
          uploadAcceptedMimetype={props.uploadAcceptedMimetype}
          addInBulk={props.addInBulk}
          shouldLoadDataUrlImage={props.shouldLoadDataUrlImage}
          onClose={onClose}
          uploadVectors={showVectors}
          defaultTab={props.defaultTab}
          mediaTypesToShow={mediaTypesToShow}
          hideSocialMediaOptions={hideSocialMediaOptions()}
          onClickRecordOptions={onClickRecordOptions}
          maxFilesToUpload={props.maxSelectionNumber}
          onClickAiImage={(e) => {
            e.stopPropagation();
            setPanelToShow(UserMediaPanelType.AI_IMAGE);
          }}
          onClickAiVoice={(e) => {
            e.stopPropagation();
            setPanelToShow(UserMediaPanelType.AI_VOICE);
          }}
          addSelectedItems={(items) => {
            props.onAddSelected(items ?? []);
          }}
        />
        {isInCollection() || isShowingBackgroundImages() ? null : (
          <TabsSection isSmallScreen={params.small} defaultTab={props.defaultTab} mediaTypesToShow={mediaTypesToShow} defaultActiveSubTab={props.defaultActiveSubTab} />
        )}
        {getGrids()}
      </div>
    );
  };

  if (isFbPanelOpen) {
    return (
      <FacebookMediaSection
        onStartAddSelected={onStartAddSelected}
        onClose={onClose}
        maxSelectionNumber={props.maxSelectionNumber}
        onAddSelected={(item: GridUserMediaItemStorage): void => {
          props.onAddSelected(gridItemsToElementData([item]));
        }}
      />
    );
  }

  const getDefaultPanel = (): ReactElement => {
    return (
      <Panel
        panelId={panelId.current}
        panelHeader={getHeader()}
        panelContent={getContent()}
        panelFooter={
          <MediaPanelFooter
            onAddSelectedCTAText={props.onAddSelectedCTAText}
            isSmallScreen={params.small}
            defaultTab={props.defaultTab}
            mediaTypesToShow={mediaTypesToShow}
            bottomPills={props.bottomPills}
            onAddSelected={addSelectedItems}
            onClose={onClose}
            hideSecondaryActions={hideFooterSecondaryActions}
          />
        }
        ref={containerRef}
      />
    );
  };

  const onBackFromRecordPanels = (): void => {
    setPanelToShow(UserMediaPanelType.DEFAULT);

    if (params.small) {
      updateOptionsPanelHeight(panelId.current, '800px');
    }
  };

  const getRecordAudioPanel = (): ReactElement => {
    return (
      <RecordAudioPanel
        onStartAddSelected={onStartAddSelected}
        onAddSelectedCTAText={props.onAddSelectedCTAText}
        onAddSelected={(data): void => {
          props.onAddSelected([data]);
        }}
        panelId={panelId.current}
        onClose={onClose}
        onBack={onBackFromRecordPanels}
        collectionId={currentCollectionId}
      />
    );
  };

  const getRecordImageAndVideoPanel = (preference: RecorderMediaType): ReactElement => {
    return (
      <RecordImageAndVideoPanel
        onStartAddSelected={onStartAddSelected}
        onAddSelectedCTAText={props.onAddSelectedCTAText}
        onAddSelected={(data): void => {
          props.onAddSelected([data]);
        }}
        recorderOpenedWithMediaType={preference}
        onBack={(): void => {
          setPanelToShow(UserMediaPanelType.DEFAULT);
        }}
        onClose={onClose}
        panelId={panelId.current}
        hideToggle={currentCollectionId !== ''}
        collectionId={currentCollectionId}
      />
    );
  };

  const getPanelForAiImage = (): ReactElement => {
    return (
      <StockPanel
        panelId={panelId.current}
        onStartAddSelected={onStartAddSelected}
        onBack={() => {
          setPanelToShow(UserMediaPanelType.DEFAULT);
        }}
        onAddSelected={(data, currentUploadIndex, totalUploadsCount): void => {
          props.onAddSelected(data, currentUploadIndex, totalUploadsCount);
        }}
        addInBulk
        maxSelectionNumber={props.maxSelectionNumber}
        showTextToImageTool
        shouldLoadDataUrlImage={props.shouldLoadDataUrlImage}
        defaultSearchTab={StockPanelTabs.TEXT_TO_IMAGE}
        tabsToShow={[StockPanelTabs.TEXT_TO_IMAGE]}
        onClose={onClose}
      />
    );
  };

  const getPanelForAiVoice = (): ReactElement => {
    return (
      <AiVoicePanel
        contentClassname={params.large ? 'spacing-p-t-8 spacing-p-l-10 spacing-p-r-10' : undefined}
        onAddAudioItem={async (item): Promise<void> => {
          onClose();
          await addGeneratedVoiceToDesign(Number(item.id));
        }}
        onClose={onClose}
        panelId={getAiVoicePanelId(panelId.current)}
        onBack={() => {
          setPanelToShow(UserMediaPanelType.DEFAULT);
        }}
      />
    );
  };

  const getPanels = (): ReactElement => {
    switch (panelToShow) {
      case UserMediaPanelType.AI_IMAGE:
        return getPanelForAiImage();
      case UserMediaPanelType.AI_VOICE:
        return getPanelForAiVoice();
      case UserMediaPanelType.RECORD_AUDIO:
        return getRecordAudioPanel();
      case UserMediaPanelType.RECORD_PHOTO:
        return getRecordImageAndVideoPanel(RecorderMediaType.IMAGE);
      case UserMediaPanelType.RECORD_VIDEO:
        return getRecordImageAndVideoPanel(RecorderMediaType.VIDEO);
      case UserMediaPanelType.DEFAULT:
      default:
        return getDefaultPanel();
    }
  };

  return getPanels();
}

export default UserMediaPanel;
